export interface CollaborationRuntimeAccess {
  canOpenInstallationThread: boolean;
  status:
    | 'not_imported'
    | 'instance_unavailable'
    | 'standard_external'
    | 'ready'
    | 'unbound'
    | 'blocked_other_parent'
    | 'blocked_partner_mismatch';
  message: string;
  boundParentTenantId: string | null;
  boundPartnerId: string | null;
}

export type CollaborationFindingEditableFieldKey = 'description' | 'reference' | 'rootCause' | 'evidence';
export type CollaborationActionEditableFieldKey = 'title' | 'status' | 'ownerEmail' | 'targetDate';
export type CollaborationFindingFieldOwnership = 'source_owned' | 'collaborator_eligible';
export type CollaborationFieldUpdateImportStatus = 'pending_import' | 'imported';
export type CollaborationSourceUpdateAckStatus = 'pending_ack' | 'acknowledged';
export type CollaborationSourceUpdateMode = 'acknowledge_only' | 'published_new_baseline';
export type CollaborationSourceUpdateFieldResolutionMode = 'use_source' | 'keep_mine' | 'manual_merge';

export interface CollaborationThread {
  threadId: string;
  shareInvitationId: string;
  rootObjectType: 'finding' | 'action';
  rootObjectId: string;
  titleSnapshot: string;
  summarySnapshot: string | null;
  status: 'active' | 'suspended' | 'recalled' | 'closed';
  createdBy: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface CollaborationThreadParticipant {
  participantId: string;
  participantType:
    | 'origin_internal_user'
    | 'partner_contact'
    | 'invited_recipient'
    | 'external_collaborator'
    | 'covered_tenant_user'
    | 'system';
  participantRole: 'sender' | 'recipient' | 'participant' | 'system';
  partnerId: string | null;
  contactId: string | null;
  emailNormalized: string | null;
  displayNameSnapshot: string | null;
  isActive: boolean;
}

export interface CollaborationThreadWorkItem {
  workItemId: string;
  parentWorkItemId: string | null;
  objectType: 'finding' | 'action';
  objectId: string;
  titleSnapshot: string | null;
  workingState: 'active' | 'closed' | 'recalled' | 'returned_pending_adoption' | 'returned_pending_field_import' | 'source_update_pending_ack';
  lastOriginVersionToken: string | null;
}

export interface CollaborationThreadMessage {
  messageId: string;
  messageType: 'dialogue' | 'system';
  body: string;
  originType: 'internal_user' | 'external_collaborator' | 'covered_tenant_user' | 'system';
  originDisplayName: string | null;
  originEmail: string | null;
  sourceChannel:
    | 'portal_external'
    | 'portal_internal'
    | 'portal_covered_tenant'
    | 'spfx_handoff'
    | 'spfx_supplier_response'
    | 'import'
    | 'webhook'
    | 'system';
  createdAtUtc: string;
  /** Stored machine translations beside the original (tracker #57). Absent on older payloads. */
  translations?: CollaborationThreadMessageTranslation[];
}

export interface CollaborationThreadMessageTranslation {
  translationId: string;
  messageId: string;
  targetLanguage: string;
  sourceLanguage: string | null;
  translatedBody: string;
  provider: string;
  characterCount: number;
  createdAtUtc: string;
  createdBy: string | null;
  /** The original message was edited after this translation was made. */
  sourceOutdated: boolean;
}

/**
 * Target languages offered on thread messages: the product's own four
 * localisation packs (tracker #57). Codes are Azure AI Translator codes and
 * must match the platform API registry.
 */
export const COLLABORATION_TRANSLATION_LANGUAGES: ReadonlyArray<{ code: string; label: string }> = [
  { code: 'en', label: 'English' },
  { code: 'fr', label: 'French' },
  { code: 'fr-ca', label: 'French (Canada)' },
  { code: 'es', label: 'Spanish' }
];

export const translationLanguageLabel = (code: string): string =>
  COLLABORATION_TRANSLATION_LANGUAGES.find((entry) => entry.code === String(code || '').trim().toLowerCase())?.label
  ?? String(code || '').toUpperCase();

export interface CollaborationThreadSyncPackage {
  syncPackageId: string;
  workItemId: string;
  packageType: 'field_update' | 'action_proposal' | 'request_close' | 'source_update' | 'artifact_return' | 'artifact_delivery' | 'self_assessment_response';
  status: 'draft' | 'pending_review' | 'accepted' | 'rejected' | 'withdrawn' | 'rejected_technical_failure' | 'stale_conflict';
  proposalJson: string | null;
  submittedAtUtc: string | null;
  reviewedAtUtc: string | null;
  reviewedBy: string | null;
  reviewNote: string | null;
  fieldImportStatus: CollaborationFieldUpdateImportStatus | null;
  fieldImportDecisionJson: string | null;
  fieldImportedBy: string | null;
  fieldImportedAtUtc: string | null;
  sourceUpdateAckStatus: CollaborationSourceUpdateAckStatus | null;
  sourceUpdateAcknowledgedBy: string | null;
  sourceUpdateAcknowledgedAtUtc: string | null;
  createdBy: string | null;
  createdAtUtc: string;
  updatedAtUtc: string;
}

export interface CollaborationThreadArtifact {
  artifactId: string;
  syncPackageId: string | null;
  workItemId: string | null;
  artifactKind: 'message_attachment' | 'returned_evidence' | 'source_evidence';
  fileName: string;
  contentType: string | null;
  sizeBytes: number;
  quarantineStatus: 'quarantined' | 'released' | 'rejected_technical_failure';
  isRequiredForReview: boolean;
  attestationVerdict: 'clean' | 'suspicious' | 'malicious' | 'unknown' | null;
  quarantineReason: string | null;
  releasedAtUtc: string | null;
  releasedBy: string | null;
  binaryPurgedAtUtc: string | null;
  binaryPurgedBy: string | null;
  binaryPurgeReason: string | null;
  createdAtUtc: string;
}

export interface CollaborationReturnedActionProposal {
  returnedActionProposalId: string;
  sourceSyncPackageId: string;
  parentWorkItemId: string;
  proposalStatus: 'returned_pending_adoption' | 'adopted' | 'declined' | 'withdrawn_by_collaborator';
  proposalJson: string;
  titleSnapshot: string | null;
  summarySnapshot: string | null;
  submittedBy: string | null;
  reviewedBy: string | null;
  reviewedAtUtc: string | null;
  withdrawnBy: string | null;
  withdrawnAtUtc: string | null;
  adoptedOriginObjectId: string | null;
  adoptedAtUtc: string | null;
  importedBy: string | null;
  importedAtUtc: string | null;
  createdAtUtc: string;
}

export interface CollaborationThreadEvent {
  eventId: string;
  shareInvitationId: string | null;
  eventType: string;
  eventDataJson: string | null;
  actor: string | null;
  correlationId: string | null;
  createdAtUtc: string;
}

export interface CollaborationThreadSnapshot {
  thread: CollaborationThread;
  fieldUpdateContext: {
    editableFindingFieldKeys: CollaborationFindingEditableFieldKey[];
    findingFieldSnapshot: Partial<Record<CollaborationFindingEditableFieldKey, string>>;
    actionFieldContextsByObjectId: Record<
      string,
      {
        editableActionFieldKeys: CollaborationActionEditableFieldKey[];
        actionFieldSnapshot: Partial<Record<CollaborationActionEditableFieldKey, string>>;
      }
    >;
  } | null;
  participants: CollaborationThreadParticipant[];
  workItems: CollaborationThreadWorkItem[];
  messages: CollaborationThreadMessage[];
  syncPackages: CollaborationThreadSyncPackage[];
  artifacts: CollaborationThreadArtifact[];
  returnedActionProposals: CollaborationReturnedActionProposal[];
  events: CollaborationThreadEvent[];
  /** Tracker #117: started under the Free text-only allowance; the supplier replies without files. Absent reads as false. */
  textOnly?: boolean;
}

export type RecipientSyncPackageSubmissionType = Exclude<
  CollaborationThreadSyncPackage['packageType'],
  'source_update' | 'artifact_return' | 'artifact_delivery' | 'self_assessment_response'
>;

export interface RecipientSyncPackageFormState {
  workItemId: string;
  packageType: RecipientSyncPackageSubmissionType;
  summary: string;
  findingFieldUpdates: Record<CollaborationFindingEditableFieldKey, string>;
  actionFieldUpdates: Record<CollaborationActionEditableFieldKey, string>;
  actionProposalTitle: string;
  actionProposalDescription: string;
  actionProposalOwnerEmail: string;
  actionProposalTargetDate: string;
  actionProposalStatus: string;
  actionProposalVerificationPersonEmail: string;
  actionProposalVerificationNotes: string;
  actionProposalMitigatesRisk: boolean;
  filesRequired: boolean;
  files: File[];
}

export interface ActionProposalDetails {
  title?: string;
  description?: string;
  summary?: string;
  ownerEmail?: string;
  targetDate?: string;
  status?: string;
  verificationPersonEmail?: string;
  verificationNotes?: string;
  mitigatesFindingRisk?: boolean;
}

export interface FindingFieldUpdateDetail {
  fieldKey: CollaborationFindingEditableFieldKey;
  currentValue: string | null;
  proposedValue: string | null;
  fieldOwnership?: CollaborationFindingFieldOwnership | null;
}

export interface ActionFieldUpdateDetail {
  fieldKey: CollaborationActionEditableFieldKey;
  currentValue: string | null;
  proposedValue: string | null;
  fieldOwnership?: CollaborationFindingFieldOwnership | null;
}

export interface FindingSourceUpdateReviewField {
  fieldKey: CollaborationFindingEditableFieldKey;
  previousValue: string;
  sourceValue: string;
  draftValue: string;
  hasCollaboratorDraft: boolean;
  hasConflict: boolean;
}

export interface ActionSourceUpdateReviewField {
  fieldKey: CollaborationActionEditableFieldKey;
  previousValue: string;
  sourceValue: string;
  draftValue: string;
  hasCollaboratorDraft: boolean;
  hasConflict: boolean;
}

export const relativeTime = (value: string): string => {
  const deltaSeconds = Math.floor((Date.now() - new Date(value).getTime()) / 1000);
  if (!Number.isFinite(deltaSeconds)) return 'unknown';
  if (deltaSeconds < 60) return 'just now';
  if (deltaSeconds < 3600) return `${Math.floor(deltaSeconds / 60)} minutes ago`;
  if (deltaSeconds < 86400) return `${Math.floor(deltaSeconds / 3600)} hours ago`;
  return `${Math.floor(deltaSeconds / 86400)} days ago`;
};

export const formatBytes = (value: number): string => {
  if (!Number.isFinite(value) || value <= 0) return '0 B';
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  return `${(value / (1024 * 1024)).toFixed(1)} MB`;
};

export const parseJsonSafe = (value: string): Record<string, unknown> => {
  try {
    const parsed = JSON.parse(value);
    if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
      return parsed as Record<string, unknown>;
    }
  } catch {
    // no-op
  }
  return {};
};

export const defaultRecipientSyncPackageFormState = (): RecipientSyncPackageFormState => ({
  workItemId: '',
  packageType: 'field_update',
  summary: '',
  findingFieldUpdates: {
    description: '',
    reference: '',
    rootCause: '',
    evidence: ''
  },
  actionFieldUpdates: {
    title: '',
    status: '',
    ownerEmail: '',
    targetDate: ''
  },
  actionProposalTitle: '',
  actionProposalDescription: '',
  actionProposalOwnerEmail: '',
  actionProposalTargetDate: '',
  actionProposalStatus: '',
  actionProposalVerificationPersonEmail: '',
  actionProposalVerificationNotes: '',
  actionProposalMitigatesRisk: false,
  filesRequired: true,
  files: []
});

export const recipientSyncPackageFormHasFieldUpdateContent = (
  form: Pick<RecipientSyncPackageFormState, 'summary' | 'findingFieldUpdates' | 'actionFieldUpdates'>
): boolean =>
  Boolean(
    String(form.summary || '').trim() ||
      Object.values(form.findingFieldUpdates).some((value) => Boolean(String(value || '').trim())) ||
      Object.values(form.actionFieldUpdates).some((value) => Boolean(String(value || '').trim()))
  );

export function buildRecipientFieldUpdateProposalFromForm(
  form: Pick<RecipientSyncPackageFormState, 'summary' | 'findingFieldUpdates' | 'actionFieldUpdates'>,
  objectType: 'finding',
  fieldSnapshot: Partial<Record<CollaborationFindingEditableFieldKey, string>> | null | undefined
): {
  summary: string;
  fieldUpdates: FindingFieldUpdateDetail[];
};

export function buildRecipientFieldUpdateProposalFromForm(
  form: Pick<RecipientSyncPackageFormState, 'summary' | 'findingFieldUpdates' | 'actionFieldUpdates'>,
  objectType: 'action',
  fieldSnapshot: Partial<Record<CollaborationActionEditableFieldKey, string>> | null | undefined
): {
  summary: string;
  fieldUpdates: ActionFieldUpdateDetail[];
};

export function buildRecipientFieldUpdateProposalFromForm(
  form: Pick<RecipientSyncPackageFormState, 'summary' | 'findingFieldUpdates' | 'actionFieldUpdates'>,
  objectType: CollaborationThreadWorkItem['objectType'],
  fieldSnapshot:
    | Partial<Record<CollaborationFindingEditableFieldKey, string>>
    | Partial<Record<CollaborationActionEditableFieldKey, string>>
    | null
    | undefined
): {
  summary: string;
  fieldUpdates: Array<FindingFieldUpdateDetail | ActionFieldUpdateDetail>;
} {
  if (objectType === 'action') {
    const actionSnapshot = fieldSnapshot as Partial<Record<CollaborationActionEditableFieldKey, string>> | null | undefined;
    return {
      summary: String(form.summary || '').trim(),
      fieldUpdates: Object.entries(form.actionFieldUpdates)
        .map(([fieldKey, proposedValue]) => ({
          fieldKey: fieldKey as CollaborationActionEditableFieldKey,
          currentValue: actionSnapshot?.[fieldKey as CollaborationActionEditableFieldKey] ?? null,
          proposedValue: String(proposedValue || '').trim() || null
        }))
        .filter((entry) => ['title', 'status', 'ownerEmail', 'targetDate'].includes(entry.fieldKey) && entry.proposedValue !== null)
    };
  }

  const findingSnapshot = fieldSnapshot as Partial<Record<CollaborationFindingEditableFieldKey, string>> | null | undefined;
  return {
    summary: String(form.summary || '').trim(),
    fieldUpdates: Object.entries(form.findingFieldUpdates)
      .map(([fieldKey, proposedValue]) => ({
        fieldKey: fieldKey as CollaborationFindingEditableFieldKey,
        currentValue: findingSnapshot?.[fieldKey as CollaborationFindingEditableFieldKey] ?? null,
        proposedValue: String(proposedValue || '').trim() || null
      }))
      .filter((entry) => entry.proposedValue !== null)
  };
}

export const getFindingSourceUpdateReviewFields = (
  sourceUpdatePackage: CollaborationThreadSyncPackage | null,
  form: Pick<RecipientSyncPackageFormState, 'findingFieldUpdates'>
): FindingSourceUpdateReviewField[] => {
  if (!sourceUpdatePackage?.proposalJson) {
    return [];
  }
  return extractFindingFieldUpdates(parseJsonSafe(sourceUpdatePackage.proposalJson)).map((entry) => {
    const sourceValue = entry.proposedValue ?? '';
    const previousValue = entry.currentValue ?? '';
    const draftValue = String(form.findingFieldUpdates[entry.fieldKey] || '');
    const hasCollaboratorDraft = draftValue.trim().length > 0;
    return {
      fieldKey: entry.fieldKey,
      previousValue,
      sourceValue,
      draftValue,
      hasCollaboratorDraft,
      hasConflict: hasCollaboratorDraft && draftValue !== sourceValue
    };
  });
};

export const getActionSourceUpdateReviewFields = (
  sourceUpdatePackage: CollaborationThreadSyncPackage | null,
  form: Pick<RecipientSyncPackageFormState, 'actionFieldUpdates'>
): ActionSourceUpdateReviewField[] => {
  if (!sourceUpdatePackage?.proposalJson) {
    return [];
  }
  return extractActionFieldUpdates(parseJsonSafe(sourceUpdatePackage.proposalJson)).map((entry) => {
    const sourceValue = entry.proposedValue ?? '';
    const previousValue = entry.currentValue ?? '';
    const draftValue = String(form.actionFieldUpdates[entry.fieldKey] || '');
    const hasCollaboratorDraft = draftValue.trim().length > 0;
    return {
      fieldKey: entry.fieldKey,
      previousValue,
      sourceValue,
      draftValue,
      hasCollaboratorDraft,
      hasConflict: hasCollaboratorDraft && draftValue !== sourceValue
    };
  });
};

const optionalTrimmedValue = (...values: unknown[]): string | undefined => {
  for (const value of values) {
    if (typeof value === 'string') {
      const normalized = value.trim();
      if (normalized) {
        return normalized;
      }
    }
  }
  return undefined;
};

export const extractActionProposalDetails = (payload: Record<string, unknown>): ActionProposalDetails => {
  const nested =
    payload.actionProposal && typeof payload.actionProposal === 'object' && !Array.isArray(payload.actionProposal)
      ? (payload.actionProposal as Record<string, unknown>)
      : {};
  const nestedRisk = nested.mitigatesFindingRisk;
  const rootRisk = payload.mitigatesFindingRisk;
  return {
    title: optionalTrimmedValue(nested.title, payload.title, payload.actionTitle),
    description: optionalTrimmedValue(nested.description, payload.description),
    summary: optionalTrimmedValue(payload.summary, payload.notes),
    ownerEmail: optionalTrimmedValue(nested.ownerEmail, payload.ownerEmail),
    targetDate: optionalTrimmedValue(nested.targetDate, payload.targetDate),
    status: optionalTrimmedValue(nested.status, payload.status),
    verificationPersonEmail: optionalTrimmedValue(
      nested.verificationPersonEmail,
      payload.verificationPersonEmail
    ),
    verificationNotes: optionalTrimmedValue(nested.verificationNotes, payload.verificationNotes),
    mitigatesFindingRisk:
      typeof nestedRisk === 'boolean'
        ? nestedRisk
        : typeof rootRisk === 'boolean'
          ? rootRisk
          : undefined
  };
};

export const getWorkItemSubmissionBlockReason = (
  workItem: CollaborationThreadWorkItem | null | undefined
): string | null => {
  if (!workItem) {
    return 'The selected work item is no longer available in this supplier quality thread.';
  }
  if (workItem.workingState === 'recalled') {
    return 'This supplier quality work item has been recalled by the owner and is no longer accepting sync packages.';
  }
  if (workItem.workingState === 'closed') {
    return 'This supplier quality work item is closed and no longer accepting sync packages.';
  }
  if (workItem.workingState === 'returned_pending_adoption') {
    return 'This supplier quality work item already has a returned Action awaiting owner decision before more packages can be submitted.';
  }
  if (workItem.workingState === 'returned_pending_field_import') {
    return 'This supplier quality work item already has returned field updates awaiting source review before more packages can be submitted.';
  }
  if (workItem.workingState === 'source_update_pending_ack') {
    return 'The source has published an update for this work item. Acknowledge that update before more packages can be submitted.';
  }
  return null;
};

export const describeWorkItemState = (workItem: CollaborationThreadWorkItem): string | null => {
  if (workItem.workingState === 'recalled') {
    return 'Owner recalled this work item. Reopen it before suppliers submit more packages.';
  }
  if (workItem.workingState === 'closed') {
    return 'This work item is closed. New supplier quality packages are blocked.';
  }
  if (workItem.workingState === 'returned_pending_adoption') {
    return 'A returned Action proposal is awaiting owner decision for this work item.';
  }
  if (workItem.workingState === 'returned_pending_field_import') {
    return 'Returned field updates are awaiting source review for this work item.';
  }
  if (workItem.workingState === 'source_update_pending_ack') {
    return 'A source-authored update is awaiting supplier acknowledgement for this work item.';
  }
  return null;
};

const FINDING_FIELD_UPDATE_KEYS: CollaborationFindingEditableFieldKey[] = [
  'description',
  'reference',
  'rootCause',
  'evidence'
];

export const findingFieldUpdateLabel = (fieldKey: CollaborationFindingEditableFieldKey): string => {
  switch (fieldKey) {
    case 'description':
      return 'Description';
    case 'reference':
      return 'Reference';
    case 'rootCause':
      return 'Root cause';
    case 'evidence':
      return 'Evidence';
    default:
      return fieldKey;
  }
};

export const actionFieldUpdateLabel = (fieldKey: CollaborationActionEditableFieldKey): string => {
  switch (fieldKey) {
    case 'title':
      return 'Title';
    case 'status':
      return 'Status';
    case 'ownerEmail':
      return 'Owner';
    case 'targetDate':
      return 'Target date';
    default:
      return fieldKey;
  }
};

export const extractFindingFieldUpdates = (payload: Record<string, unknown>): FindingFieldUpdateDetail[] =>
  (Array.isArray(payload.fieldUpdates) ? payload.fieldUpdates : []).reduce<FindingFieldUpdateDetail[]>((acc, entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return acc;
    }
    const record = entry as Record<string, unknown>;
    const fieldKey = String(record.fieldKey || '').trim();
    if (!FINDING_FIELD_UPDATE_KEYS.includes(fieldKey as CollaborationFindingEditableFieldKey)) {
      return acc;
    }
    acc.push({
      fieldKey: fieldKey as CollaborationFindingEditableFieldKey,
      currentValue: record.currentValue === null || record.currentValue === undefined ? null : String(record.currentValue),
      proposedValue: record.proposedValue === null || record.proposedValue === undefined ? null : String(record.proposedValue),
      fieldOwnership:
        String(record.fieldOwnership || '').trim() === 'source_owned'
          ? 'source_owned'
          : String(record.fieldOwnership || '').trim() === 'collaborator_eligible'
            ? 'collaborator_eligible'
            : null
    });
    return acc;
  }, []);

export const extractActionFieldUpdates = (payload: Record<string, unknown>): ActionFieldUpdateDetail[] =>
  (Array.isArray(payload.fieldUpdates) ? payload.fieldUpdates : []).reduce<ActionFieldUpdateDetail[]>((acc, entry) => {
    if (!entry || typeof entry !== 'object' || Array.isArray(entry)) {
      return acc;
    }
    const record = entry as Record<string, unknown>;
    const fieldKey = String(record.fieldKey || '').trim();
    if (!['title', 'status', 'ownerEmail', 'targetDate'].includes(fieldKey)) {
      return acc;
    }
    acc.push({
      fieldKey: fieldKey as CollaborationActionEditableFieldKey,
      currentValue: record.currentValue === null || record.currentValue === undefined ? null : String(record.currentValue),
      proposedValue: record.proposedValue === null || record.proposedValue === undefined ? null : String(record.proposedValue),
      fieldOwnership:
        String(record.fieldOwnership || '').trim() === 'source_owned'
          ? 'source_owned'
          : String(record.fieldOwnership || '').trim() === 'collaborator_eligible'
            ? 'collaborator_eligible'
            : null
    });
    return acc;
  }, []);

export const extractSourceUpdateMode = (payload: Record<string, unknown>): CollaborationSourceUpdateMode | null => {
  const value = String(payload.updateMode || '').trim();
  return value === 'acknowledge_only' || value === 'published_new_baseline' ? value : null;
};

export const getLatestDraftFieldUpdatePackage = (
  syncPackages: CollaborationThreadSyncPackage[],
  workItemId: string,
  actorEmail?: string | null
): CollaborationThreadSyncPackage | null =>
  syncPackages
    .filter(
      (entry) =>
        entry.workItemId === workItemId &&
        entry.packageType === 'field_update' &&
        entry.status === 'draft' &&
        String(entry.createdBy || '').trim().toLowerCase() === String(actorEmail || '').trim().toLowerCase()
    )
    .sort((left, right) => new Date(right.updatedAtUtc || right.createdAtUtc).getTime() - new Date(left.updatedAtUtc || left.createdAtUtc).getTime())[0] ?? null;

export const getLatestPendingSourceUpdatePackage = (
  syncPackages: CollaborationThreadSyncPackage[],
  workItemId: string
): CollaborationThreadSyncPackage | null =>
  syncPackages
    .filter(
      (entry) =>
        entry.workItemId === workItemId &&
        entry.packageType === 'source_update' &&
        entry.sourceUpdateAckStatus === 'pending_ack'
    )
    .sort((left, right) => new Date(right.updatedAtUtc || right.createdAtUtc).getTime() - new Date(left.updatedAtUtc || left.createdAtUtc).getTime())[0] ?? null;

export const populateRecipientSyncPackageFormFromFieldUpdateDraft = (
  workItemId: string,
  draftPackage: CollaborationThreadSyncPackage | null
): RecipientSyncPackageFormState => {
  const nextForm = defaultRecipientSyncPackageFormState();
  nextForm.workItemId = workItemId;
  if (!draftPackage?.proposalJson) {
    return nextForm;
  }
  const payload = parseJsonSafe(draftPackage.proposalJson);
  const fieldUpdates = extractFindingFieldUpdates(payload);
  nextForm.packageType = 'field_update';
  nextForm.summary = optionalTrimmedValue(payload.summary) || '';
  fieldUpdates.forEach((fieldUpdate) => {
    nextForm.findingFieldUpdates[fieldUpdate.fieldKey] = fieldUpdate.proposedValue ?? '';
  });
  extractActionFieldUpdates(payload).forEach((fieldUpdate) => {
    nextForm.actionFieldUpdates[fieldUpdate.fieldKey] = fieldUpdate.proposedValue ?? '';
  });
  return nextForm;
};

export const describeThreadEvent = (event: CollaborationThreadEvent): string => {
  const data = event.eventDataJson ? parseJsonSafe(event.eventDataJson) : {};
  const objectLabel = optionalTrimmedValue(
    typeof data.title === 'string' ? data.title : undefined,
    typeof data.objectId === 'string'
      ? `${optionalTrimmedValue(data.objectType) || 'item'} ${String(data.objectId)}`
      : undefined
  );
  switch (event.eventType) {
    case 'thread_initialized':
      return 'Collaboration thread initialized.';
    case 'share_response_received':
      return `Recipient responded via share link: ${optionalTrimmedValue(data.status) || 'updated'}.`;
    case 'inbox_item_triage_accepted':
      return 'Supplier accepted the inbound item from their portal inbox.';
    case 'inbox_item_triage_rejected':
      return 'Supplier rejected the inbound item from their portal inbox.';
    case 'inbox_item_archived':
      return 'Supplier archived the inbound item.';
    case 'inbox_baseline_latest_updated':
      return 'Source baseline update is available for the supplier.';
    case 'inbox_baseline_pulled':
      return 'Supplier pulled the latest source baseline.';
    case 'inbox_baseline_acknowledged':
      return 'Supplier acknowledged the pulled source baseline.';
    case 'share_imported':
      return 'Share imported into the recipient environment.';
    case 'share_revoked':
      return 'Share revoked by the owner.';
    case 'sync_package_submitted':
      return `Sync package submitted${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'sync_package_reviewed':
      return `Sync package ${optionalTrimmedValue(data.decision) || 'reviewed'}${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'sync_package_withdrawn':
      return `${optionalTrimmedValue(data.initiatedBy)?.replace(/_/g, ' ') || 'Package'} withdrew a sync package${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'sync_package_stale_marked':
      return `Sync package marked stale${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'work_item_origin_version_refreshed':
      return data.bulkRefresh
        ? `All work items refreshed (${Number(data.refreshedWorkItemCount || 0)} total).`
        : `Origin version refreshed${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'work_item_state_changed':
      return `Work item state changed from ${optionalTrimmedValue(data.previousState) || 'unknown'} to ${optionalTrimmedValue(data.nextState) || 'unknown'}${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'artifact_quarantined':
      return `Returned artifact quarantined: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'artifact_released':
      return `Returned artifact released: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'artifact_downloaded':
      return `Returned artifact downloaded: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'artifact_imported':
      return `Returned artifact imported as source evidence: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'artifact_technical_failure':
      return `Returned artifact failed technical quarantine: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'artifact_binary_purged':
      return `Returned artifact binary purged: ${optionalTrimmedValue(data.fileName) || 'attachment'}.`;
    case 'source_update_published':
      return `Source update published${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'source_update_acknowledged':
      return `Source update acknowledged${objectLabel ? ` for ${objectLabel}` : ''}.`;
    case 'returned_action_recorded':
      return `Returned Action proposal recorded${optionalTrimmedValue(data.title) ? `: ${String(data.title)}` : '.'}`;
    case 'returned_action_adopted':
      return 'Returned Action proposal adopted.';
    case 'returned_action_declined':
      return 'Returned Action proposal declined.';
    case 'returned_action_withdrawn':
      return 'Returned Action proposal withdrawn.';
    case 'finding_field_update_imported':
      return 'Returned Finding field updates applied into the authoritative source.';
    case 'action_field_update_imported':
      return 'Returned Action field updates applied into the authoritative source.';
    default:
      return event.eventType.replace(/_/g, ' ');
  }
};

export const canWithdrawActiveSyncPackage = (
  syncPackage: Pick<CollaborationThreadSyncPackage, 'status' | 'createdBy'>,
  actorEmail?: string | null
): boolean =>
  (syncPackage.status === 'draft' || syncPackage.status === 'pending_review') &&
  String(syncPackage.createdBy || '').trim().toLowerCase() === String(actorEmail || '').trim().toLowerCase();
